import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  BookOpen,
  Play,
  Users,
  Radio,
  Trophy,
  User,
  Sparkles,
  Flame,
  ArrowRight,
  Globe,
  Compass,
  Clock,
  CheckCircle2,
  Sun,
  Moon,
  Crown,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Download,
  MessageSquare,
  MessageSquareHeart,
  HeartHandshake,
  Copy,
  Check,
  ShieldAlert,
  Bell,
  X,
} from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import {
  UI_TRANSLATIONS,
  LANGUAGE_META,
  SupportedLanguage,
  getLocalizedCategoryLabel,
} from './i18n/translations.ts';
import { BIBLE_CATEGORIES } from './data/verifiedQuestions.ts';
import { SoloQuizView } from './components/SoloQuizView.tsx';
import { MultiplayerView } from './components/MultiplayerView.tsx';
import { LiveEventView } from './components/LiveEventView.tsx';
import { RankingsAndTournamentsView } from './components/RankingsAndTournamentsView.tsx';
import { ProfileAndAdminView } from './components/ProfileAndAdminView.tsx';
import {
  PWAInstallCard,
  OfflineIndicator,
} from './components/PWAInstallBanner.tsx';
import { soundEngine } from './utils/soundAndSpeech.ts';
import { TermsOfUseView } from './components/legal/TermsOfUseView.tsx';
import { PrivacyNoticeView } from './components/legal/PrivacyNoticeView.tsx';
import { CommunityGuidelinesView } from './components/legal/CommunityGuidelinesView.tsx';
import { CommunityFeedView } from './components/CommunityFeedView.tsx';
import { BrandLogo } from './components/BrandLogo.tsx';
import { FeedbackModal } from './components/FeedbackModal.tsx';
import { ReportModal } from './components/ReportModal.tsx';
import { ShareToFeedModal } from './components/ShareToFeedModal.tsx';
import { UserProfileModal } from './components/UserProfileModal.tsx';
import { AdSenseBanner } from './components/AdSenseBanner.tsx';
import { AdminPanelView } from './components/AdminPanelView.tsx';
import { supabase } from './lib/supabase.ts';
import { ProfileProvider, useProfile } from './context/ProfileContext.tsx';
import { UserAvatar } from './components/UserAvatar.tsx';
import { Top10PlayersCard } from './components/Top10PlayersCard.tsx';
import { FriendsAndChatView } from './components/FriendsAndChatView.tsx';
import { GlobalRankerMarquee } from './components/GlobalRankerMarquee.tsx';
import { PlatformFooter } from './components/PlatformFooter.tsx';
import { QuickBibleMascot } from './components/QuickBibleMascot.tsx';

type PrimarySection =
  | 'home'
  | 'play'
  | 'multiplayer'
  | 'feed'
  | 'friends'
  | 'global_chat'
  | 'live'
  | 'rankings'
  | 'profile'
  | 'admin'
  | 'terms'
  | 'privacy'
  | 'guidelines';

const SECTIONS_ORDER: PrimarySection[] = [
  'home',
  'play',
  'multiplayer',
  'feed',
  'friends',
  'global_chat',
  'live',
  'rankings',
  'profile',
  'admin',
  'terms',
  'privacy',
  'guidelines',
];

const sectionMotionVariants = {
  enter: () => ({
    opacity: 0,
    y: 4,
  }),
  center: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.18,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
  exit: () => ({
    opacity: 0,
    y: -2,
    transition: {
      duration: 0.12,
      ease: [0.4, 0, 1, 1] as const,
    },
  }),
};

function parseSectionFromHash(rawHash: string): PrimarySection | null {
  const clean = rawHash.replace(/^#/, '').trim().toLowerCase();
  if (!clean) return null;
  if (clean.startsWith('friends')) return 'friends';
  if (clean.startsWith('global-chat') || clean.startsWith('global_chat') || clean.startsWith('chat')) return 'global_chat';
  if (clean.startsWith('community') || clean.startsWith('feed')) return 'feed';
  if (clean.startsWith('ranks') || clean.startsWith('rankings') || clean.startsWith('rank-game')) return 'rankings';
  if (clean.startsWith('match') || clean.startsWith('multiplayer')) return 'multiplayer';
  if (clean.startsWith('live')) return 'live';
  if (clean.startsWith('play')) return 'play';
  if (clean.startsWith('profile')) return 'profile';
  if (clean.startsWith('admin')) return 'admin';
  if (clean.startsWith('terms')) return 'terms';
  if (clean.startsWith('privacy')) return 'privacy';
  if (clean.startsWith('guidelines')) return 'guidelines';
  if (clean.startsWith('home')) return 'home';

  const base = clean.split('-')[0];
  if (
    [
      'home',
      'play',
      'multiplayer',
      'feed',
      'friends',
      'global_chat',
      'live',
      'rankings',
      'profile',
      'admin',
      'terms',
      'privacy',
      'guidelines',
    ].includes(base)
  ) {
    return base as PrimarySection;
  }
  return null;
}

function getHashForSection(section: PrimarySection): string {
  if (section === 'feed') return '#community';
  if (section === 'friends') return '#friends';
  if (section === 'global_chat') return '#global-chat';
  if (section === 'rankings') return '#ranks';
  if (section === 'multiplayer') return '#match';
  return `#${section}`;
}

function getInitialView(): PrimarySection {
  if (typeof window !== 'undefined') {
    if (window.location.pathname === '/admin') return 'admin';

    // 1. Restore view from URL hash
    const fromHash = parseSectionFromHash(window.location.hash);
    if (fromHash) return fromHash;

    // 2. Restore view from sessionStorage
    const fromStorage = sessionStorage.getItem('qb_active_view');
    if (fromStorage) {
      const parsed = parseSectionFromHash(fromStorage);
      if (parsed) return parsed;
    }
  }
  // 3. Default to Home only if neither exists
  return 'home';
}

export default function App() {
  const [[activeSection, direction], setSectionWithDirection] = useState<
    [PrimarySection, number]
  >(() => [getInitialView(), 0]);

  const [appToast, setAppToast] = useState<{
    message: string;
    type: 'info' | 'success' | 'error';
  } | null>(null);

  const showToast = React.useCallback(
    (message: string, type: 'info' | 'success' | 'error' = 'info') => {
      setAppToast({ message, type });
      setTimeout(() => {
        setAppToast((prev) => (prev?.message === message ? null : prev));
      }, 3800);
    },
    []
  );

  const [platformSettings, setPlatformSettings] = useState<{
    maintenanceMode: boolean;
    announcementBanner: string;
    signupsEnabled: boolean;
  }>({
    maintenanceMode: false,
    announcementBanner: '',
    signupsEnabled: true,
  });

  const navigateToSection = (newSection: PrimarySection) => {
    if (newSection === activeSection) return;
    const oldIndex = SECTIONS_ORDER.indexOf(activeSection);
    const newIndex = SECTIONS_ORDER.indexOf(newSection);
    const dir = newIndex > oldIndex ? 1 : -1;

    const isSocialHubSwitch =
      (activeSection === 'friends' && newSection === 'global_chat') ||
      (activeSection === 'global_chat' && newSection === 'friends');

    if (typeof window !== 'undefined') {
      sessionStorage.setItem('qb_active_view', newSection);
      const hashVal = getHashForSection(newSection);

      if (newSection === 'admin' && window.location.pathname !== '/admin') {
        window.history.pushState({}, '', '/admin' + hashVal);
      } else if (newSection !== 'admin' && window.location.pathname === '/admin') {
        window.history.pushState({}, '', '/' + hashVal);
      } else {
        if (window.location.hash !== hashVal) {
          window.history.pushState({}, '', hashVal);
        }
      }

      if (!isSocialHubSwitch && window.scrollY > 40) {
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      }
    }

    setSectionWithDirection([newSection, dir]);
  };

  const [language, setLanguage] = useState<SupportedLanguage>(() => {
    const saved = localStorage.getItem('qb_language');
    return (saved as SupportedLanguage) || 'en';
  });

  const [userUid, setUserUid] = useState<string>(() => {
    const existing = localStorage.getItem('qb_scholar_uid');
    if (existing) return existing;
    const generated = `scholar-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem('qb_scholar_uid', generated);
    return generated;
  });

  const [supabaseUser, setSupabaseUser] = useState<any | null>(null);
  const supabaseUserRef = React.useRef<any | null>(null);
  const languageRef = React.useRef<SupportedLanguage>(language);

  const [bootstrapData, setBootstrapData] = useState<any | null>(null);
  const [quizPreset, setQuizPreset] = useState<{
    mode: string;
    subMode?: string;
    category?: string;
    questionFormat?: string;
    difficulty?: string;
    autoStart?: boolean;
  } | null>(null);
  const [projectorPinFromUrl, setProjectorPinFromUrl] = useState<
    string | null
  >(null);
  const [isQuizPlaying, setIsQuizPlaying] = useState(false);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{
    type: 'profile_picture' | 'feed_post' | 'feed_comment';
    id: string;
    targetUserUid: string;
    label?: string;
  } | null>(null);
  const [milestonePrompt, setMilestonePrompt] = useState<{
    defaultContent: string;
    badgeCode?: string;
    postType?: string;
    score?: number;
    accuracy?: number;
    bestStreak?: number;
    xpGained?: number;
    userRank?: string;
    churchName?: string;
  } | null>(null);

  // User profile modal state
  const [viewingUserProfileUid, setViewingUserProfileUid] = useState<string | null>(null);

  // Notifications state & Realtime subscription
  const [notificationsList, setNotificationsList] = useState<
    Array<{
      id: number;
      userId: string;
      actorId: string;
      actorName: string;
      actorAvatar?: string;
      type: 'reaction' | 'comment' | 'friend_request' | 'friend_accept' | string;
      reactionType?: string;
      postId: number;
      commentId?: number | null;
      commentPreview?: string;
      postPreview?: string;
      title: string;
      message: string;
      isRead: boolean;
      createdAt: string;
    }>
  >([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const notificationsMenuRef = React.useRef<HTMLDivElement>(null);
  const languageMenuRef = React.useRef<HTMLDivElement>(null);
  const [focusedFeedPostId, setFocusedFeedPostId] = useState<number | null>(null);
  const [appSocket, setAppSocket] = useState<Socket | null>(null);
  const [friendsBadgeCounts, setFriendsBadgeCounts] = useState<{
    pendingRequests: number;
    unreadPrivate: number;
  }>({ pendingRequests: 0, unreadPrivate: 0 });

  const unreadNotificationsCount = React.useMemo(
    () => notificationsList.filter((n) => !n.isRead).length,
    [notificationsList]
  );

  const loadNotifications = React.useCallback(async (targetUid: string) => {
    if (!targetUid) return;
    try {
      const token = localStorage.getItem('qb_auth_token') || '';
      const headers: Record<string, string> = {};
      if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(
        `/api/notifications?uid=${encodeURIComponent(targetUid)}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.notifications)) {
          setNotificationsList(data.notifications);
        }
      }
    } catch {
      // Ignore offline error
    }
  }, []);

  useEffect(() => {
    const activeUid = supabaseUser?.id || userUid;
    if (!activeUid) return;
    loadNotifications(activeUid);

    // 1. Supabase Realtime subscription on public.notifications (RLS-scoped to user_id)
    const channel = supabase
      .channel(`notifications-realtime-${activeUid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${activeUid}`,
        },
        (payload: any) => {
          if (payload?.eventType === 'INSERT' && payload?.new) {
            const row = payload.new;
            const mapped = {
              id: Number(row.id || Date.now()),
              userId: row.user_id || row.user_uid || activeUid,
              actorId: row.actor_id || '',
              actorName: row.actor_name || 'Scholar',
              actorAvatar: row.actor_avatar || '',
              type: (row.type === 'comment' ? 'comment' : 'reaction') as
                | 'reaction'
                | 'comment',
              reactionType: row.reaction_type || '',
              postId: Number(row.post_id || 1),
              commentId: row.comment_id ? Number(row.comment_id) : null,
              commentPreview: row.comment_preview || '',
              postPreview: row.post_preview || '',
              title: row.title || '',
              message: row.message || '',
              isRead: Boolean(row.is_read),
              createdAt: row.created_at || new Date().toISOString(),
            };
            setNotificationsList((prev) => {
              if (prev.some((n) => n.id === mapped.id)) return prev;
              return [mapped, ...prev];
            });
          } else {
            loadNotifications(activeUid);
          }
        }
      )
      .subscribe();

    // 2. Socket.IO live listener for immediate Express/PostgreSQL notification & social events
    const socket = io({ transports: ['websocket', 'polling'] });
    setAppSocket(socket);
    socket.emit('presence:register', {
      userUid: activeUid,
      username:
        bootstrapData?.account?.user?.username ||
        supabaseUser?.user_metadata?.username ||
        'Scholar',
    });
    socket.on('notification:new', (item: any) => {
      if (item && item.userId === activeUid) {
        setNotificationsList((prev) => {
          if (prev.some((n) => n.id === item.id)) return prev;
          return [item, ...prev];
        });
      }
    });

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        // Ignore
      }
      socket.disconnect();
      setAppSocket(null);
    };
  }, [supabaseUser?.id, userUid, loadNotifications]);

  // Close notifications dropdown on outside click
  useEffect(() => {
    if (!isNotificationsOpen) return;
    const handleClickOutside = (ev: MouseEvent) => {
      if (
        notificationsMenuRef.current &&
        !notificationsMenuRef.current.contains(ev.target as Node)
      ) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationsOpen]);

  const handleMarkAllNotificationsRead = async () => {
    soundEngine.playClick();
    const activeUid = supabaseUser?.id || userUid;
    setNotificationsList((prev) => prev.map((n) => ({ ...n, isRead: true })));
    const token = localStorage.getItem('qb_auth_token') || '';
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      await fetch('/api/notifications/read', {
        method: 'POST',
        headers,
        body: JSON.stringify({ userUid: activeUid, notificationId: 'all' }),
      });
    } catch {
      // Ignore
    }
  };

  const handleOpenNotificationPost = async (item: {
    id: number;
    postId: number;
    isRead: boolean;
    type?: string;
    actorId?: string;
  }) => {
    soundEngine.playClick();
    const activeUid = supabaseUser?.id || userUid;
    if (!item.isRead) {
      setNotificationsList((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n))
      );
      const token = localStorage.getItem('qb_auth_token') || '';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
        headers.Authorization = `Bearer ${token}`;
      }

      fetch('/api/notifications/read', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userUid: activeUid,
          notificationId: item.id,
        }),
      }).catch(() => {});
    }
    setIsNotificationsOpen(false);
    if (item.type === 'friend_request' || item.type === 'friend_accept') {
      if (item.actorId) {
        setViewingUserProfileUid(item.actorId);
      } else {
        navigateToSection('profile');
      }
      return;
    }
    setFocusedFeedPostId(item.postId || 1);
    navigateToSection('feed');
  };

  const handleOpenSharePrompt = (
    contentOrPayload:
      | string
      | {
          defaultContent: string;
          badgeCode?: string;
          postType?: string;
          score?: number;
          accuracy?: number;
          bestStreak?: number;
          xpGained?: number;
          userRank?: string;
          churchName?: string;
        },
    badgeCode?: string
  ) => {
    if (typeof contentOrPayload === 'string') {
      setMilestonePrompt({
        defaultContent: contentOrPayload,
        badgeCode,
      });
    } else {
      setMilestonePrompt(contentOrPayload);
    }
  };
  const [copiedSupport, setCopiedSupport] = useState<string | null>(null);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const copySupportInfo = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSupport(key);
    setTimeout(() => setCopiedSupport(null), 2500);
  };

  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;

  // Enforce Dark Mode permanently
  useEffect(() => {
    document.documentElement.classList.add('dark');
    localStorage.setItem('qb_theme', 'dark');
  }, []);

  // Reset window scroll when active section changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [activeSection]);

  useEffect(() => {
    languageRef.current = language;
    localStorage.setItem('qb_language', language);
  }, [language]);

  const isRegisteredUserEmail = (email?: string | null) => {
    if (!email) return false;
    const clean = String(email).trim().toLowerCase();
    return (
      clean.includes('@') &&
      !clean.endsWith('@quickbible.global') &&
      !clean.endsWith('@quickybible.global')
    );
  };

  const loadBootstrap = async (lang = languageRef.current, overrideUid?: string) => {
    const activeUid = overrideUid || userUid;
    try {
      const token = localStorage.getItem('qb_auth_token') || '';
      const headers: Record<string, string> = {};
      if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(
        `/api/bootstrap?uid=${encodeURIComponent(
          activeUid
        )}&lang=${encodeURIComponent(lang)}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        if (data?.platformSettings) {
          setPlatformSettings({
            maintenanceMode: Boolean(data.platformSettings.maintenanceMode),
            announcementBanner: data.platformSettings.announcementBanner || '',
            signupsEnabled: data.platformSettings.signupsEnabled !== false,
          });
        }
        if (typeof window !== 'undefined' && data?.adsenseConfig) {
          if (data.adsenseConfig.clientId) {
            window.__QB_ADSENSE_CLIENT_ID__ = data.adsenseConfig.clientId;
          }
          if (data.adsenseConfig.slots) {
            window.__QB_ADSENSE_SLOTS__ = data.adsenseConfig.slots;
          }
        }
        setBootstrapData((prev: any) => {
          const hasAuthToken = Boolean(localStorage.getItem('qb_auth_token'));
          const activeSupaUser = supabaseUserRef.current || supabaseUser;
          const isIncomingRegistered = isRegisteredUserEmail(
            data?.account?.user?.email
          );

          // Never overwrite an authenticated account with the guest account.
          // If supabaseUser exists OR localStorage has qb_auth_token, keep the current account
          // and only update leaderboards/tournaments.
          if ((activeSupaUser || hasAuthToken) && !isIncomingRegistered) {
            const preservedAccount =
              prev?.account && isRegisteredUserEmail(prev?.account?.user?.email)
                ? prev.account
                : activeSupaUser
                ? {
                    ...(prev?.account || {}),
                    user: {
                      uid: activeSupaUser.id,
                      email: activeSupaUser.email,
                      displayName:
                        activeSupaUser.user_metadata?.display_name ||
                        activeSupaUser.email?.split('@')[0] ||
                        'Scholar',
                      username:
                        activeSupaUser.user_metadata?.username ||
                        activeSupaUser.email?.split('@')[0] ||
                        'scholar',
                      avatarUrl:
                        activeSupaUser.user_metadata?.avatar_url ||
                        activeSupaUser.user_metadata?.picture ||
                        prev?.account?.user?.avatarUrl ||
                        prev?.account?.profile?.avatarUrl ||
                        localStorage.getItem('qb_user_avatar') ||
                        '',
                      country: activeSupaUser.user_metadata?.country || 'PH',
                      role: 'scholar',
                      xp: 0,
                      level: 1,
                    },
                  }
                : prev?.account;

            if (preservedAccount) {
              return {
                ...data,
                account: preservedAccount,
                leaderboards: data.leaderboards,
                tournaments: data.tournaments,
              };
            }
          }
          if (data?.account?.user?.avatarUrl) {
            localStorage.setItem('qb_user_avatar', data.account.user.avatarUrl);
          }
          return data;
        });
      }
    } catch {
      // Offline / fallback handled gracefully
    }
  };

  useEffect(() => {
    loadBootstrap(language, userUid);
  }, [language, userUid]);

  const fetchProfileInBackground = React.useCallback((session: any) => {
    if (!session?.user?.id || !session?.user?.email) return;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (
      session.access_token &&
      !String(session.access_token).startsWith('auth-active-') &&
      !String(session.access_token).startsWith('verified-')
    ) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }

    const storedAvatar = localStorage.getItem('qb_user_avatar') || '';
    const avatarToSync =
      session.user.user_metadata?.avatar_url ||
      session.user.user_metadata?.picture ||
      bootstrapData?.account?.user?.avatarUrl ||
      bootstrapData?.account?.profile?.avatarUrl ||
      storedAvatar;

    fetch('/api/auth/sync', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        uid: session.user.id,
        email: session.user.email,
        username: session.user.user_metadata?.username,
        displayName:
          session.user.user_metadata?.display_name ||
          session.user.user_metadata?.full_name ||
          session.user.user_metadata?.name,
        avatarUrl: avatarToSync,
        country: session.user.user_metadata?.country,
        language: languageRef.current,
        theme: 'dark',
      }),
      signal: controller.signal,
    })
      .then(async (res) => {
        clearTimeout(timeoutId);
        if (res.ok) {
          const synced = await res.json();
          console.log('[Profile Fetch Result] Success:', synced?.user?.email);
          if (synced?.user?.avatarUrl) {
            localStorage.setItem('qb_user_avatar', synced.user.avatarUrl);
          }
          setBootstrapData((prev: any) =>
            prev ? { ...prev, account: synced } : { account: synced }
          );
        } else {
          console.warn(
            '[Profile Fetch Result] Non-ok status:',
            res.status,
            '- continuing with default profile'
          );
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        console.warn(
          '[Profile Fetch Result] Network/Timeout:',
          err?.name === 'AbortError' ? 'Timed out after 5s' : err?.message,
          '- continuing with default profile'
        );
      });
  }, []);

  const handleActiveSession = React.useCallback(
    (session: any, accountOverride?: any) => {
      const resolvedUser =
        session?.user ||
        (accountOverride?.user
          ? {
              id: accountOverride.user.uid,
              email: accountOverride.user.email,
              email_confirmed_at: new Date().toISOString(),
              user_metadata: {
                username: accountOverride.user.username,
                display_name: accountOverride.user.displayName,
                country: accountOverride.user.country,
                avatar_url: accountOverride.user.avatarUrl,
              },
            }
          : null);

      console.log('[Auth Session] handleActiveSession called:', {
        sessionFound: Boolean(session),
        userId: resolvedUser?.id,
        email: resolvedUser?.email,
      });

      if (!resolvedUser?.id || !resolvedUser?.email) return;

      const previousUid = supabaseUserRef.current?.id;
      const isSameUser = previousUid === resolvedUser.id;

      supabaseUserRef.current = resolvedUser;
      setSupabaseUser(resolvedUser);
      if (session?.access_token) {
        localStorage.setItem('qb_auth_token', session.access_token);
      } else if (!localStorage.getItem('qb_auth_token')) {
        localStorage.setItem('qb_auth_token', `auth-active-${Date.now()}`);
      }
      localStorage.setItem('qb_scholar_uid', resolvedUser.id);
      setUserUid(resolvedUser.id);

      // Ignore re-fetch / re-init for already-active user
      if (isSameUser && bootstrapData?.account?.user?.uid === resolvedUser.id) {
        console.log('[Auth Session] Silent update for active user:', resolvedUser.email);
        return;
      }

      const resolvedAvatar =
        accountOverride?.user?.avatarUrl ||
        accountOverride?.profile?.avatarUrl ||
        resolvedUser.user_metadata?.avatar_url ||
        resolvedUser.user_metadata?.picture ||
        bootstrapData?.account?.user?.avatarUrl ||
        bootstrapData?.account?.profile?.avatarUrl ||
        localStorage.getItem('qb_user_avatar') ||
        '';

      if (resolvedAvatar) {
        localStorage.setItem('qb_user_avatar', resolvedAvatar);
      }

      // Populate account state synchronously so UI leaves guest screen immediately
      const defaultUser = {
        uid: resolvedUser.id,
        email: resolvedUser.email,
        displayName:
          accountOverride?.user?.displayName ||
          resolvedUser.user_metadata?.display_name ||
          resolvedUser.email?.split('@')[0] ||
          'Scholar',
        username:
          accountOverride?.user?.username ||
          resolvedUser.user_metadata?.username ||
          resolvedUser.email?.split('@')[0] ||
          'scholar',
        avatarUrl: resolvedAvatar,
        country:
          accountOverride?.user?.country ||
          resolvedUser.user_metadata?.country ||
          'PH',
        role: accountOverride?.user?.role || 'scholar',
        xp: accountOverride?.user?.xp ?? 0,
        level: accountOverride?.user?.level ?? 1,
      };

      setBootstrapData((prev: any) => {
        const mergedAccount = accountOverride
          ? { ...(prev?.account || {}), ...accountOverride }
          : prev?.account && isRegisteredUserEmail(prev?.account?.user?.email)
          ? { ...prev.account }
          : {};
        const mergedUser = {
          ...defaultUser,
          ...(mergedAccount.user || {}),
          email: resolvedUser.email,
          uid: resolvedUser.id,
        };
        const mergedProfile = {
          favoriteVerse: 'Psalm 119:105',
          churchOrOrg: 'Global Berean Community',
          ...(mergedAccount.profile || {}),
        };
        const mergedLevel = mergedAccount.levelInfo || {
          level: mergedUser.level || 1,
          rankTitle: 'Scripture Seeker',
          progressPercent: 0,
        };

        return {
          ...prev,
          account: {
            ...mergedAccount,
            user: mergedUser,
            profile: mergedProfile,
            levelInfo: mergedLevel,
          },
        };
      });

      // Non-blocking background fetch for full database profile
      fetchProfileInBackground({
        ...(session || {}),
        user: resolvedUser,
      });
    },
    [fetchProfileInBackground, bootstrapData?.account?.user?.uid]
  );

  // Register supabase.auth.onAuthStateChange and getSession() once on mount (null-safe)
  useEffect(() => {
    (async () => {
      try {
        const res = await supabase.auth.getSession();
        const session = res?.data?.session ?? null;
        console.log(
          '[Auth Session] Initial getSession():',
          session?.user ? session.user.email : 'No active session'
        );
        if (session?.user && supabaseUserRef.current?.id !== session.user.id) {
          handleActiveSession(session);
        }
      } catch (err: any) {
        console.warn('[Auth Session] getSession() error:', err?.message || err);
      }
    })();

    const subRes = supabase.auth.onAuthStateChange((event, session) => {
      console.log(
        '[Auth State Change]',
        event,
        session?.user?.email || 'No user'
      );

      // Handle TOKEN_REFRESHED and USER_UPDATED as silent session updates only
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (session?.access_token) {
          localStorage.setItem('qb_auth_token', session.access_token);
        }
        if (session?.user) {
          supabaseUserRef.current = session.user;
          setSupabaseUser(session.user);
        }
        return;
      }

      if (
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
        session?.user
      ) {
        // Ignore SIGNED_IN events when session user ID matches already-active user
        if (supabaseUserRef.current?.id === session.user.id) {
          console.log('[Auth State Change] Ignoring SIGNED_IN event for already-active user');
          if (session?.access_token) {
            localStorage.setItem('qb_auth_token', session.access_token);
          }
          return;
        }
        handleActiveSession(session);
      } else if (event === 'SIGNED_OUT') {
        console.log('[Auth State Change] User signed out');
        supabaseUserRef.current = null;
        setSupabaseUser(null);
        localStorage.removeItem('qb_auth_token');
        localStorage.removeItem('qb_scholar_uid');
        sessionStorage.removeItem('qb_active_view');
        if (typeof window !== 'undefined') {
          window.location.hash = '';
        }
        const guestUid = `guest-${Math.random().toString(36).substring(2, 10)}`;
        setUserUid(guestUid);
        loadBootstrap(languageRef.current, guestUid);
        setSectionWithDirection(['home', -1]);
      }
    });

    const subscription = subRes?.data?.subscription;
    return () => {
      subscription?.unsubscribe?.();
    };
  }, [handleActiveSession]);

  // Listen to hashchange so browser Back / Forward buttons work properly
  useEffect(() => {
    const handleHashChange = () => {
      const parsed = parseSectionFromHash(window.location.hash);
      if (parsed && parsed !== activeSection) {
        const oldIndex = SECTIONS_ORDER.indexOf(activeSection);
        const newIndex = SECTIONS_ORDER.indexOf(parsed);
        const dir = newIndex > oldIndex ? 1 : -1;
        sessionStorage.setItem('qb_active_view', parsed);
        setSectionWithDirection([parsed, dir]);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeSection]);

  // Sync activeSection to sessionStorage and window hash
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('qb_active_view', activeSection);
      const targetHash = getHashForSection(activeSection);
      const currentParsed = parseSectionFromHash(window.location.hash);
      if (!window.location.hash || currentParsed !== activeSection) {
        const path = activeSection === 'admin' ? '/admin' : '/';
        window.history.replaceState({}, '', path + targetHash);
      }
    }
  }, [activeSection]);

  const username =
    bootstrapData?.account?.user?.username || supabaseUser?.user_metadata?.username || 'BereanScholar';
  const country = bootstrapData?.account?.user?.country || 'PH';
  const levelInfo = bootstrapData?.account?.levelInfo || {
    level: 1,
    rankTitle: 'Scripture Seeker',
    progressPercent: 25,
  };

  const isRegisteredAccount = Boolean(
    (supabaseUser && isRegisteredUserEmail(supabaseUser.email)) ||
    (bootstrapData?.account?.user?.email && isRegisteredUserEmail(bootstrapData.account.user.email))
  );

  const [supabaseRoleIsAdmin, setSupabaseRoleIsAdmin] = useState(false);

  useEffect(() => {
    const activeUid = supabaseUser?.id || bootstrapData?.account?.user?.uid;
    if (!activeUid) {
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('uid', activeUid)
          .maybeSingle();

        if (error) {
          console.warn('[Role Fetch Error] Failed to fetch profile role:', error.message);
          // Handle role-fetch errors gracefully: keep current view & role, never navigate on failure
          return;
        }

        if (data?.role === 'admin') {
          setSupabaseRoleIsAdmin(true);
        } else if (data?.role && data.role !== 'admin') {
          setSupabaseRoleIsAdmin(false);
        }
      } catch (err: any) {
        console.warn('[Role Fetch Exception] Gracefully caught role fetch error:', err?.message);
        // Handle role-fetch errors gracefully: keep current view & role, never navigate on failure
      }
    })();
  }, [supabaseUser?.id, bootstrapData?.account?.user?.uid]);

  const activeEmailLower = String(
    supabaseUser?.email || bootstrapData?.account?.user?.email || ''
  )
    .trim()
    .toLowerCase();

  const isAdmin = Boolean(
    supabaseRoleIsAdmin ||
      bootstrapData?.account?.user?.role === 'admin' ||
      bootstrapData?.account?.profile?.role === 'admin' ||
      activeEmailLower === 'adonizchoy@gmail.com' ||
      activeEmailLower === 'melencionmichelle1991@gmail.com' ||
      activeEmailLower === 'adonismelencion26@gmail.com'
  );

  // Enforce /admin route guard: redirect non-admins once bootstrapData has loaded
  useEffect(() => {
    if (activeSection === 'admin' && bootstrapData !== null && !isAdmin) {
      if (
        activeEmailLower === 'adonizchoy@gmail.com' ||
        activeEmailLower === 'melencionmichelle1991@gmail.com' ||
        activeEmailLower === 'adonismelencion26@gmail.com'
      ) {
        setSupabaseRoleIsAdmin(true);
        return;
      }
      if (typeof window !== 'undefined' && window.location.pathname === '/admin') {
        window.history.replaceState({}, '', '/');
      }
      setSectionWithDirection(['home', -1]);
      showToast('Access restricted: Administrator role required for /admin.', 'error');
    }
  }, [activeSection, bootstrapData, isAdmin, activeEmailLower, showToast]);

  // Listen to browser popstate for /admin
  useEffect(() => {
    const onPopState = () => {
      if (window.location.pathname === '/admin') {
        setSectionWithDirection(['admin', 1]);
      } else if (activeSection === 'admin') {
        setSectionWithDirection(['home', -1]);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activeSection]);

  // Synchronize Quiz Mode audio lifecycle so background music only plays during active quiz sessions
  useEffect(() => {
    const isQuizActive = activeSection === 'play' && isQuizPlaying;
    soundEngine.setQuizMode(isQuizActive);
  }, [activeSection, isQuizPlaying]);

  const launchPresetQuiz = (preset: {
    mode: string;
    subMode?: string;
    category?: string;
    questionFormat?: string;
    difficulty?: string;
    autoStart?: boolean;
  }) => {
    if (platformSettings.maintenanceMode && !isAdmin) {
      showToast(
        'Quick!Bible is currently in Maintenance Mode. Gameplay will resume shortly.',
        'error'
      );
      return;
    }
    soundEngine.playClick();
    setQuizPreset(preset);
    navigateToSection('play');
  };

  const activeUid = supabaseUser?.id || userUid;
  const currentAvatarUrl =
    bootstrapData?.account?.user?.avatarUrl ||
    bootstrapData?.account?.profile?.avatarUrl ||
    localStorage.getItem('qb_user_avatar') ||
    '';

  return (
    <ProfileProvider userUid={activeUid} initialAvatarUrl={currentAvatarUrl}>
      <div className="app-shell min-h-[100dvh] flex flex-col bg-black text-[#FAFAFA] selection:bg-[#262626] selection:text-white transition-colors duration-200">
      {/* HEADER: LOGO + DESKTOP NAV + USER PROFILE + TOOLBAR */}
      <header className="flex-shrink-0 sticky top-0 z-40 bg-[#0A0A0A]/95 text-[#FAFAFA] border-b border-[#27272A] backdrop-blur-md px-3 box-border overflow-visible shadow-sm">
        <div className="w-full max-w-full flex items-center justify-between flex-nowrap h-12">
          {/* Zone 1: Branding Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              soundEngine.playClick();
              navigateToSection('home');
            }}
            className="flex items-center gap-2 focus:outline-none shrink-0 min-w-0"
            aria-label="Quick!Bible Home"
          >
            <BrandLogo size="sm" showWordmark wordmarkClassName="text-[16px] sm:text-xl" />
          </a>

          {/* Zone 2: Centered Desktop Navigation Links (Web PC view only) */}
          <nav className="hidden lg:flex items-center space-x-6 text-xs font-semibold text-[#A3A3A3] absolute left-1/2 -translate-x-1/2">
            <button
              type="button"
              onClick={() => { soundEngine.playClick(); navigateToSection('home'); }}
              className={`hover:text-white transition-colors cursor-pointer ${activeSection === 'home' ? 'text-[#F97316] font-bold' : ''}`}
            >
              Home
            </button>
            <button
              type="button"
              onClick={() => { soundEngine.playClick(); navigateToSection('feed'); }}
              className={`hover:text-white transition-colors cursor-pointer ${activeSection === 'feed' ? 'text-[#F97316] font-bold' : ''}`}
            >
              Community Feed
            </button>
            <button
              type="button"
              onClick={() => { soundEngine.playClick(); navigateToSection('friends'); }}
              className={`relative hover:text-white transition-colors cursor-pointer inline-flex items-center gap-1 ${activeSection === 'friends' ? 'text-[#F97316] font-bold' : ''}`}
            >
              <span>My Friends</span>
              {(friendsBadgeCounts.pendingRequests + friendsBadgeCounts.unreadPrivate) > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-rose-500 text-white text-[9px] font-black">
                  {friendsBadgeCounts.pendingRequests + friendsBadgeCounts.unreadPrivate}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => { soundEngine.playClick(); navigateToSection('global_chat'); }}
              className={`hover:text-white transition-colors cursor-pointer ${activeSection === 'global_chat' ? 'text-[#F97316] font-bold' : ''}`}
            >
              Global Chat
            </button>
            <button
              type="button"
              onClick={() => { soundEngine.playClick(); navigateToSection('rankings'); }}
              className={`hover:text-white transition-colors cursor-pointer ${activeSection === 'rankings' ? 'text-[#F97316] font-bold' : ''}`}
            >
              Rank Game
            </button>
            <button
              type="button"
              onClick={() => { soundEngine.playClick(); navigateToSection('live'); }}
              className={`hover:text-white transition-colors cursor-pointer ${activeSection === 'live' ? 'text-[#F97316] font-bold' : ''}`}
            >
              Host Live Event
            </button>
          </nav>

          {/* Right Header Controls */}
          <div className="flex items-center gap-1 shrink-0">
            {/* My Friends & Global Chat Header Quick Buttons (Visible on Mobile/Tablet & Desktop) */}
            <button
              type="button"
              onClick={() => {
                soundEngine.playClick();
                navigateToSection('friends');
              }}
              className={`relative w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                activeSection === 'friends'
                  ? 'bg-[#F97316] text-black'
                  : 'bg-[#141414] hover:bg-[#1C1C1C] border border-[#262626] text-[#D4D4D4] hover:text-white'
              }`}
              aria-label="My Friends & Private Messenger"
              title="My Friends & Private Chat"
            >
              <Users className="w-4 h-4" />
              {(friendsBadgeCounts.pendingRequests + friendsBadgeCounts.unreadPrivate) > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[9px] font-extrabold font-mono flex items-center justify-center">
                  {friendsBadgeCounts.pendingRequests + friendsBadgeCounts.unreadPrivate}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                soundEngine.playClick();
                navigateToSection('global_chat');
              }}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                activeSection === 'global_chat'
                  ? 'bg-[#F97316] text-black'
                  : 'bg-[#141414] hover:bg-[#1C1C1C] border border-[#262626] text-[#D4D4D4] hover:text-white'
              }`}
              aria-label="Global Chat"
              title="Global Community Chat"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            {/* Admin Button (Restore) */}
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  soundEngine.playClick();
                  navigateToSection('admin');
                }}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                  activeSection === 'admin'
                    ? 'bg-[#F97316] text-black'
                    : 'bg-[#141414] hover:bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] hover:text-white'
                }`}
                aria-label="Admin Panel"
                title="Admin Console"
              >
                <ShieldCheck className="w-4 h-4" />
              </button>
            )}
            
            {/* Language Selector */}
            <div className="relative" ref={languageMenuRef}>
              <button
                type="button"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-[#141414] hover:bg-[#1C1C1C] border border-[#262626] hover:border-[#404040] text-[#FAFAFA] transition-colors"
                aria-label="Select Language"
              >
                <Globe className="w-4 h-4 text-[#D4D4D4]" />
              </button>
              {isLanguageMenuOpen && (
                <div className="absolute right-0 top-10 w-40 rounded-xl bg-[#141414] border border-[#262626] shadow-2xl z-50 py-2">
                  {Object.entries(LANGUAGE_META).map(([code, meta]) => (
                    <button
                      key={code}
                      onClick={() => { setLanguage(code as SupportedLanguage); setIsLanguageMenuOpen(false); }}
                      className="w-full px-4 py-2 text-left text-xs font-semibold hover:bg-[#1C1C1C] flex items-center justify-between"
                    >
                      {meta.label}
                      {language === code && <Check className="w-3.5 h-3.5 text-[#F97316]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Account Control */}
            <div className="ml-1">
              {isRegisteredAccount ? (
                <button
                  type="button"
                  onClick={() => { soundEngine.playClick(); navigateToSection('profile'); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-[#141414] hover:bg-[#1C1C1C] border border-[#262626] hover:border-[#404040] transition-colors overflow-hidden"
                >
                  <UserAvatar 
                    src={currentAvatarUrl} 
                    name={username} 
                    sizeClassName="w-8 h-8"
                    versionKey={bootstrapData?.account?.user?.updatedAt || bootstrapData?.account?.user?.id}
                  />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigateToSection('profile')}
                  className="px-3 h-8 flex items-center justify-center rounded-full bg-[#F97316] hover:bg-[#FB923C] text-black text-xs font-bold transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Notifications Bell */}
            <div className="relative ml-1" ref={notificationsMenuRef}>
              <button
                type="button"
                onClick={() => { soundEngine.playClick(); setIsNotificationsOpen((prev) => !prev); }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-[#141414] hover:bg-[#1C1C1C] border border-[#262626] hover:border-[#404040] text-[#FAFAFA] transition-colors"
              >
                <Bell className="w-4 h-4 text-[#D4D4D4]" />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute top-0 right-0 min-w-[14px] h-[14px] px-0.5 rounded-full bg-[#F97316] text-black text-[9px] font-extrabold font-mono flex items-center justify-center">
                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                  </span>
                )}
              </button>
              {/* Notifications Dropdown Panel */}
              {isNotificationsOpen && (
                <div
                  data-testid="notifications-panel"
                  className="fixed sm:absolute right-3 sm:right-0 top-14 sm:top-full sm:mt-2 w-[calc(100vw-1.5rem)] sm:w-96 max-w-md rounded-2xl bg-[#141414] border border-[#262626] shadow-2xl z-50 overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between gap-2 bg-[#0A0A0A]">
                    <div className="flex items-center gap-2">
                      <Bell className="w-3.5 h-3.5 text-[#F97316]" />
                      <span className="text-xs font-extrabold uppercase tracking-wider text-[#FAFAFA]">
                        Notifications
                      </span>
                      {unreadNotificationsCount > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-[#F97316]/15 border border-[#F97316]/30 text-[#F97316] text-[10px] font-bold font-mono">
                          {unreadNotificationsCount} new
                        </span>
                      )}
                    </div>

                    {notificationsList.length > 0 && (
                      <button
                        type="button"
                        data-testid="mark-all-read-btn"
                        onClick={handleMarkAllNotificationsRead}
                        disabled={unreadNotificationsCount === 0}
                        className="text-[11px] font-semibold text-[#F97316] hover:text-[#FB923C] disabled:text-[#737373] transition-colors cursor-pointer disabled:cursor-default"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-[#262626]">
                    {notificationsList.length === 0 ? (
                      <div className="py-10 px-4 text-center space-y-1.5">
                        <Bell className="w-6 h-6 text-[#737373] mx-auto" />
                        <p className="text-xs font-semibold text-[#D4D4D4]">
                          No notifications yet
                        </p>
                        <p className="text-[11px] text-[#737373]">
                          When scholars react to or comment on your posts, they will appear here live.
                        </p>
                      </div>
                    ) : (
                      notificationsList.map((item) => {
                        const actorInitials = (item.actorName || 'SC')
                          .slice(0, 2)
                          .toUpperCase();
                        return (
                          <button
                            key={item.id}
                            type="button"
                            data-testid={`notification-item-${item.id}`}
                            onClick={() => handleOpenNotificationPost(item)}
                            className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors cursor-pointer ${
                              item.isRead
                                ? 'bg-[#141414] hover:bg-[#1C1C1C]'
                                : 'bg-[#1C1C1C] hover:bg-[#262626]'
                            }`}
                          >
                            {item.actorAvatar ? (
                              <img
                                src={item.actorAvatar}
                                alt={item.actorName}
                                className="w-8 h-8 rounded-full object-cover border border-[#262626] shrink-0 mt-0.5"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                                {actorInitials}
                              </div>
                            )}

                            <div className="min-w-0 flex-1 space-y-1">
                              <p className="text-xs text-[#FAFAFA] font-medium leading-snug break-words">
                                {item.message}
                              </p>
                              {item.postPreview && (
                                <p className="text-[11px] text-[#A3A3A3] truncate">
                                  “{item.postPreview}”
                                </p>
                              )}
                              <span className="text-[10px] font-mono text-[#737373] block">
                                {new Date(item.createdAt).toLocaleDateString(
                                  undefined,
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  }
                                )}
                              </span>
                            </div>

                            {!item.isRead && (
                              <span
                                aria-label="Unread notification"
                                className="w-2 h-2 rounded-full bg-[#F97316] shrink-0 mt-2"
                              />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* GLOBAL ANNOUNCEMENT & MAINTENANCE BANNERS */}
      {platformSettings.announcementBanner && (
        <div className="bg-[#141414] border-b border-[#F97316]/40 px-4 py-2 text-center text-xs font-semibold text-[#F5F5F5] flex items-center justify-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#F97316] shrink-0" />
          <span>{platformSettings.announcementBanner}</span>
        </div>
      )}
      {platformSettings.maintenanceMode && (
        <div className="bg-[#1C1C1C] border-b border-[#F59E0B]/50 px-4 py-2 text-center text-xs font-bold text-[#F59E0B] flex items-center justify-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#F59E0B] shrink-0" />
          <span>
            Maintenance Mode is active — Gameplay is temporarily paused for non-admin accounts.
          </span>
        </div>
      )}

      {appToast && (
        <div className="fixed top-20 right-4 z-[80] max-w-sm px-4 py-3 rounded-xl bg-[#141414] border border-[#262626] shadow-2xl flex items-center gap-2.5 text-xs font-semibold text-[#F5F5F5] animate-in fade-in slide-in-from-top-4 duration-300">
          {appToast.type === 'success' && <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" />}
          {appToast.type === 'error' && <ShieldAlert className="w-4 h-4 text-red-500 shrink-0" />}
          {appToast.type === 'info' && <Sparkles className="w-4 h-4 text-[#F97316] shrink-0" />}
          <span>{appToast.message}</span>
        </div>
      )}

      {/* MAIN VIEWPORT WITH SUBTLE FADE TRANSITIONS */}
      <main className="app-main w-full relative flex-1 flex flex-col">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={
              activeSection === 'friends' || activeSection === 'global_chat'
                ? 'social_hub'
                : activeSection
            }
            custom={direction}
            variants={sectionMotionVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full flex-1 flex flex-col"
          >
            {activeSection === 'home' && (
              <>
                {/* GLOBAL TOP RANKER ANNOUNCEMENT MARQUEE — POSITIONED DIRECTLY ABOVE HERO */}
                <GlobalRankerMarquee />

                <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 py-6 sm:py-10 space-y-6 sm:space-y-8">
                  {/* HERO SECTION WITH CINEMATIC LIGHTING & OFFICIAL MASCOT */}
                  <section className="relative w-full pt-4 sm:pt-8 pb-6 sm:pb-8 overflow-hidden rounded-2xl bg-black border border-[#262626]">
                    {/* Cinematic Golden Light Background Graphic */}
                    <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[65%] md:w-[55%] pointer-events-none select-none overflow-hidden z-0">
                      {/* Warm golden ambient glow overlay */}
                      <div className="absolute right-0 -top-12 w-64 sm:w-80 h-64 sm:h-80 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
                      <div className="absolute right-4 top-1/4 w-72 h-72 bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />

                      {/* Pure black gradient overlay ensuring crisp left headline legibility */}
                      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 sm:via-black/50 to-transparent pointer-events-none" />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black pointer-events-none" />
                    </div>

                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-center px-4 sm:px-8 pt-2 sm:pt-4">
                      {/* Left Hero Typography Content (55-60% width) */}
                      <div className="md:col-span-7 lg:col-span-7 space-y-3 sm:space-y-4 min-w-0">
                        <div className="text-xs font-semibold tracking-widest uppercase text-[#F97316]">
                          PLAY · LEARN · GROW
                        </div>

                        <h1 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-extrabold tracking-tight text-white leading-[1.08] font-sans">
                          Master Scripture<br />
                          Through <span className="text-[#F97316]">Global Play</span><br />
                          & Live Competition
                        </h1>

                        <p className="text-sm sm:text-base text-[#D4D4D4] leading-relaxed max-w-lg">
                          {t.heroSubtitle}
                        </p>
                      </div>

                      {/* Right Hero: Mascot Companion & Floating Card (40-45% width) */}
                      <div className="md:col-span-5 lg:col-span-5 relative flex flex-col items-center justify-center pt-8 sm:pt-10 md:pt-12 min-w-0">
                        <QuickBibleMascot
                          size="hero"
                          position="inline"
                          expression="happy"
                          animation="floating"
                          priority={true}
                          showGlow={true}
                          maskBottomFade={true}
                          speechBubbleText="Hey Bible Quiz Warrior, Ready to Battle?"
                          speechBubblePosition="top"
                          className="w-full max-w-[260px] sm:max-w-[320px] md:max-w-[360px]"
                          imageClassName="max-h-[220px] sm:max-h-[280px] md:max-h-[330px] object-contain"
                        />
                      </div>
                    </div>
                  </section>

                {/* ACTION CARDS SECTION MATCHING REFERENCE DESIGN */}
                <section className="space-y-3.5 sm:space-y-4">
                  {/* Primary Action Card: Play Solo Quiz */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      launchPresetQuiz({
                        mode: 'solo',
                        category: 'ALL CATEGORIES',
                        autoStart: false,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        launchPresetQuiz({
                          mode: 'solo',
                          category: 'ALL CATEGORIES',
                          autoStart: false,
                        });
                      }
                    }}
                    className="w-full bg-gradient-to-r from-[#FF8A00] to-[#EA580C] text-white p-4 sm:p-5 rounded-2xl sm:rounded-3xl shadow-lg shadow-orange-600/30 flex items-center justify-between group cursor-pointer transition-all hover:brightness-110 active:scale-[0.99] select-none"
                  >
                    <div className="flex items-center gap-4">
                      {/* Circular Play Button */}
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#141414] flex items-center justify-center text-[#F97316] shadow-md shrink-0 group-hover:scale-105 transition-transform">
                        <Play className="w-5 h-5 sm:w-6 sm:h-6 fill-[#F97316] text-[#F97316] ml-0.5" />
                      </div>
                      <div className="text-left">
                        <div className="text-lg sm:text-xl font-bold text-white tracking-tight">
                          Play Solo Quiz
                        </div>
                        <div className="text-xs sm:text-sm text-orange-100/95 mt-0.5">
                          Start a quick game and test your knowledge.
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white/90 group-hover:translate-x-1 transition-transform shrink-0 mr-1" />
                  </div>

                  {/* Secondary Cards: Global Matchmaking & Host LIVE Event */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Global Matchmaking */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        soundEngine.playClick();
                        navigateToSection('multiplayer');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          soundEngine.playClick();
                          navigateToSection('multiplayer');
                        }
                      }}
                      className="bg-[#161618] border border-[#27272A] hover:border-[#F97316]/50 p-4 sm:p-5 rounded-2xl flex items-center justify-between group cursor-pointer transition-all hover:bg-[#141414] select-none shadow-sm"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-xl bg-[#F97316]/15 text-[#F97316] flex items-center justify-center shrink-0">
                          <Users className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <div className="text-sm sm:text-base font-bold text-white tracking-tight">
                            Global Matchmaking
                          </div>
                          <div className="text-xs text-[#A3A3A3] mt-0.5">
                            Play with believers around the world.
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#F97316] group-hover:translate-x-1 transition-all shrink-0" />
                    </div>

                    {/* Host LIVE Event */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        soundEngine.playClick();
                        navigateToSection('live');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          soundEngine.playClick();
                          navigateToSection('live');
                        }
                      }}
                      className="bg-[#141414] border border-amber-900/40 hover:border-amber-600/50 p-4 sm:p-5 rounded-2xl flex items-center justify-between group cursor-pointer transition-all hover:bg-[#141414] select-none shadow-sm"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
                          <Radio className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <div className="text-sm sm:text-base font-bold text-white tracking-tight">
                            Host LIVE Event
                          </div>
                          <div className="text-xs text-amber-200/60 mt-0.5">
                            Create and manage your own Bible event.
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-amber-500/60 group-hover:text-amber-400 group-hover:translate-x-1 transition-all shrink-0" />
                    </div>
                  </div>

                  {/* Feature Proof Row: Multiple Languages | Verified Questions | Live Tournaments */}
                  <div className="bg-[#161618] border border-[#27272A] rounded-2xl p-4 sm:p-5 grid grid-cols-3 divide-x divide-[#27272A] text-center shadow-sm">
                    <div className="px-2 flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-[#F97316]/15 text-[#F97316] flex items-center justify-center mb-1.5">
                        <Globe className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-semibold text-[#FAFAFA]">
                        Multiple Languages
                      </div>
                    </div>
                    <div className="px-2 flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-[#F97316]/15 text-[#F97316] flex items-center justify-center mb-1.5">
                        <ShieldCheck className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-semibold text-[#FAFAFA]">
                        Verified Questions
                      </div>
                    </div>
                    <div className="px-2 flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-[#F97316]/15 text-[#F97316] flex items-center justify-center mb-1.5">
                        <Trophy className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-semibold text-[#FAFAFA]">
                        Live Tournaments
                      </div>
                    </div>
                  </div>
                </section>

                {/* FULL-WIDTH 4-COLUMN PLATFORM METRICS ROW */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    {
                      label: 'Unlimited Bible Questions',
                      value: '66 Books',
                      sub: 'Fresh Challenges Every Game',
                    },
                    {
                      label: 'Interactive Quiz Formats',
                      value: '7+ Formats',
                      sub: 'Multiple Choice, True/False, Matching & More',
                    },
                    {
                      label: 'Auditorium-Grade LIVE Events',
                      value: '16:9 Stage',
                      sub: 'Host + Projector + Mobile PIN',
                    },
                    {
                      label: 'Multilingual Scripture',
                      value: 'EN·TL·CEB·KO·ZH',
                      sub: 'Localized Questions & Voice Narration',
                    },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-2xl bg-[#161618] border border-[#27272A] p-5 shadow-sm"
                    >
                      <div className="text-xs text-[#A3A3A3]">
                        {stat.label}
                      </div>
                      <div className="text-xl sm:text-2xl font-bold font-mono tabular-nums text-white mt-1 truncate">
                        {stat.value}
                      </div>
                      <div className="text-xs text-[#F97316] mt-1">
                        {stat.sub}
                      </div>
                    </div>
                  ))}
                </section>

                <section className="pt-2">
                  <Top10PlayersCard
                    initialLeaderboard={bootstrapData?.leaderboards || []}
                    isRegisteredAccount={isRegisteredAccount}
                    onRequireAuth={() => navigateToSection('profile')}
                    onOpenUserProfile={(uidOrUsername) => {
                      if (!isRegisteredAccount) {
                        navigateToSection('profile');
                        return;
                      }
                      setViewingUserProfileUid(uidOrUsername);
                    }}
                    onViewFullRanks={() => navigateToSection('rankings')}
                  />
                </section>

                <AdSenseBanner placement="home" />
                </div>
              </>
            )}

        {activeSection === 'play' && (
          <SoloQuizView
            isRegisteredAccount={isRegisteredAccount}
            userUid={userUid}
            username={username}
            country={country}
            language={language}
            initialPreset={quizPreset}
            initialRankedProgression={bootstrapData?.account?.rankedProgression}
            initialChallengeProgression={bootstrapData?.account?.challengeProgression}
            onSessionCompleted={() => loadBootstrap(language)}
            onNavigateToProfile={() => navigateToSection('profile')}
            onSharePrompt={handleOpenSharePrompt}
            onPlayingStateChange={(playing) => setIsQuizPlaying(playing)}
          />
        )}

        {activeSection === 'multiplayer' && (
          <MultiplayerView
            isRegistered={isRegisteredAccount}
            userUid={userUid}
            username={username}
            avatarUrl={bootstrapData?.account?.user?.avatarUrl || null}
            country={country}
            language={language}
            onMatchFinished={() => loadBootstrap(language)}
            onNavigateToProfile={() => navigateToSection('profile')}
            onSharePrompt={handleOpenSharePrompt}
          />
        )}

        {(activeSection === 'friends' || activeSection === 'global_chat') && (
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-6 box-border flex-1 flex flex-col">
            <FriendsAndChatView
              userUid={activeUid}
              username={username}
              displayName={bootstrapData?.account?.user?.displayName || username}
              avatarUrl={currentAvatarUrl}
              rankTier={
                bootstrapData?.account?.profile?.titleBadge ||
                bootstrapData?.account?.levelInfo?.rankTitle ||
                'Seeker'
              }
              country={country}
              isRegisteredAccount={isRegisteredAccount}
              isAdmin={isAdmin}
              socket={appSocket}
              initialTab={activeSection === 'global_chat' ? 'global_chat' : 'friends'}
              onSwitchEnvironment={(env) => navigateToSection(env)}
              onRequireAuth={() => navigateToSection('profile')}
              onBadgeCountChange={(counts) => setFriendsBadgeCounts(counts)}
            />
          </div>
        )}

        {activeSection === 'feed' && (
          <CommunityFeedView
            isRegistered={isRegisteredAccount}
            currentUser={{
              uid: userUid,
              username,
              displayName: bootstrapData?.account?.user?.displayName || username,
              country,
              avatarUrl: bootstrapData?.account?.user?.avatarUrl || null,
            }}
            onNavigateToProfile={() => navigateToSection('profile')}
            onOpenReport={(target: any) => setReportTarget(target)}
            onOpenGuidelines={() => navigateToSection('guidelines')}
            focusedPostId={focusedFeedPostId}
            onClearFocusedPost={() => setFocusedFeedPostId(null)}
            onOpenUserProfile={(target) => setViewingUserProfileUid(target)}
          />
        )}

        {activeSection === 'live' && (
          <LiveEventView
            isRegistered={isRegisteredAccount}
            userUid={userUid}
            username={username}
            country={country}
            language={language}
            initialProjectorPin={projectorPinFromUrl}
            onNavigateToProfile={() => navigateToSection('profile')}
          />
        )}

        {activeSection === 'rankings' && (
          <RankingsAndTournamentsView
            isRegistered={isRegisteredAccount}
            userUid={userUid}
            username={username}
            country={country}
            language={language}
            leaderboards={bootstrapData?.leaderboards || []}
            tournaments={bootstrapData?.tournaments || []}
            onRefreshData={() => loadBootstrap(language)}
            onLaunchRankGame={() =>
              launchPresetQuiz({
                mode: 'ranked',
                category: 'ALL CATEGORIES',
                autoStart: true,
              })
            }
            onOpenUserProfile={(target) => setViewingUserProfileUid(target)}
            onNavigateToProfile={() => navigateToSection('profile')}
            onPlayTournamentQuiz={(cat, diff) =>
              launchPresetQuiz({
                mode: 'solo',
                category: cat,
                difficulty: diff,
                autoStart: true,
              })
            }
          />
        )}

        {activeSection === 'profile' && (
          <ProfileAndAdminView
            account={bootstrapData?.account}
            supabaseUser={supabaseUser}
            language={language}
            theme="dark"
            showToast={showToast}
            onLanguageChange={(lang) => setLanguage(lang)}
            onThemeChange={() => {}}
            handleActiveSession={handleActiveSession}
            onSessionEstablished={(session, account) => {
              handleActiveSession(session, account);
            }}
            onOpenUserProfile={(target) => setViewingUserProfileUid(target)}
            onAccountUpdated={(newAcc) => {
              if (newAcc?.user?.uid) {
                localStorage.setItem('qb_scholar_uid', newAcc.user.uid);
                setUserUid(newAcc.user.uid);
              }
              if (newAcc?.user?.email && isRegisteredUserEmail(newAcc.user.email)) {
                const updatedUser = {
                  id: newAcc.user.uid,
                  email: newAcc.user.email,
                  email_confirmed_at: new Date().toISOString(),
                  user_metadata: {
                    username: newAcc.user.username,
                    display_name: newAcc.user.displayName,
                    country: newAcc.user.country,
                    avatar_url: newAcc.user.avatarUrl,
                  },
                };
                supabaseUserRef.current = supabaseUserRef.current || updatedUser;
                setSupabaseUser((prev: any) => prev || updatedUser);
              }
              setBootstrapData((prev: any) =>
                prev ? { ...prev, account: newAcc } : { account: newAcc }
              );
            }}
            onAccountReset={() => {
              supabaseUserRef.current = null;
              setSupabaseUser(null);
              setBootstrapData((prev: any) =>
                prev ? { ...prev, account: null } : null
              );
              const freshUid = `scholar-${Math.random().toString(36).slice(2, 9)}`;
              localStorage.setItem('qb_scholar_uid', freshUid);
              setUserUid(freshUid);
              loadBootstrap(language, freshUid);
            }}
            onNavigateToLegal={(legalSec) => navigateToSection(legalSec)}
            onOpenReport={(target) => setReportTarget(target)}
          />
        )}

        {activeSection === 'admin' && isAdmin && (
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 w-full flex-1 flex flex-col">
            <AdminPanelView
              adminUid={supabaseUser?.id || userUid}
              adminEmail={
                supabaseUser?.email ||
                bootstrapData?.account?.user?.email ||
                'adonizchoy@gmail.com'
              }
              onExitAdmin={() => navigateToSection('home')}
              onPlatformSettingsChange={(nextSettings) =>
                setPlatformSettings(nextSettings)
              }
              showToast={showToast}
            />
          </div>
        )}

        {activeSection === 'terms' && (
          <TermsOfUseView onBack={() => navigateToSection('home')} />
        )}

        {activeSection === 'privacy' && (
          <PrivacyNoticeView onBack={() => navigateToSection('home')} />
        )}

        {activeSection === 'guidelines' && (
          <CommunityGuidelinesView onBack={() => navigateToSection('home')} />
        )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* CONSOLIDATED REUSABLE PLATFORM FOOTER */}
      {!isQuizPlaying && (
        <PlatformFooter
          onOpenAbout={() => setIsAboutModalOpen(true)}
          onOpenSupport={() => setIsSupportModalOpen(true)}
          onOpenFeedback={() => setIsFeedbackOpen(true)}
          onNavigateLegal={(legalSec) => navigateToSection(legalSec)}
        />
      )}

      {/* DOCK-STYLE MOBILE BOTTOM NAVIGATION */}
      {!isQuizPlaying && (
        <nav
          aria-label="Mobile Navigation"
          className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#121214]/95 border-t border-[#27272A] backdrop-blur-xl pb-[calc(0.6rem+env(safe-area-inset-bottom,10px))] pt-1.5 px-2 shadow-[0_-4px_25px_rgba(0,0,0,0.5)] transition-colors select-none"
        >
          <div className="grid grid-cols-7 items-center justify-items-center max-w-lg mx-auto relative gap-0.5">
            {(
              [
                { id: 'home', label: 'Home', icon: BookOpen },
                { id: 'play', label: 'Play', icon: Play },
                { id: 'multiplayer', label: 'Match', icon: Users },
                { id: 'feed', label: 'Feed', icon: MessageSquareHeart },
                { id: 'live', label: 'LIVE', icon: Radio },
                { id: 'rankings', label: 'Rank Game', icon: Trophy },
                { id: 'profile', label: 'Profile', icon: User },
              ] as const
            ).map((item) => {
              const Icon = item.icon;
              const active = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    navigateToSection(item.id);
                  }}
                  className={`relative flex flex-col items-center justify-center w-full py-1 px-0.5 rounded-xl transition-all duration-200 select-none ${
                    active
                      ? 'text-[#F97316] font-semibold'
                      : 'text-[#A1A1AA] hover:text-white font-medium'
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="mobileNavActivePill"
                      className="absolute inset-0 rounded-2xl bg-[#F97316]/15 border border-[#F97316]/30 -z-10 shadow-sm"
                      transition={{
                        type: 'spring',
                        stiffness: 450,
                        damping: 32,
                      }}
                    />
                  )}
                  <Icon
                    className={`w-4 h-4 sm:w-5 sm:h-5 mb-0.5 transition-transform duration-150 ${
                      active ? 'scale-105 stroke-[2.2]' : 'stroke-[1.75]'
                    }`}
                  />
                  <span className="text-[9px] leading-tight tracking-tight truncate max-w-full">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* FEEDBACK MODAL */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        userUid={userUid}
        userEmail={bootstrapData?.account?.user?.email}
        userName={username}
      />

      {/* MODERATION REPORT MODAL */}
      <ReportModal
        isOpen={reportTarget !== null}
        onClose={() => setReportTarget(null)}
        reporterUid={userUid}
        reporterUsername={username}
        targetType={reportTarget?.type || 'feed_post'}
        targetId={reportTarget?.id || ''}
        targetUserUid={reportTarget?.targetUserUid || ''}
        targetLabel={reportTarget?.label}
      />

      {/* SHARE TO FEED MODAL */}
      <ShareToFeedModal
        isOpen={milestonePrompt !== null}
        onClose={() => setMilestonePrompt(null)}
        userUid={userUid}
        username={username}
        userAvatar={bootstrapData?.account?.user?.avatarUrl || null}
        defaultContent={milestonePrompt?.defaultContent}
        badgeCode={milestonePrompt?.badgeCode}
        postType={milestonePrompt?.postType}
        score={milestonePrompt?.score}
        accuracy={milestonePrompt?.accuracy}
        bestStreak={milestonePrompt?.bestStreak}
        xpGained={milestonePrompt?.xpGained}
        userRank={
          milestonePrompt?.userRank ||
          bootstrapData?.account?.profile?.titleBadge ||
          bootstrapData?.account?.levelInfo?.rankTitle ||
          ''
        }
        churchName={
          milestonePrompt?.churchName ||
          bootstrapData?.account?.profile?.churchOrOrg ||
          ''
        }
        onPostCreated={() => {
          navigateToSection('feed');
        }}
      />

      {/* USER PROFILE MODAL */}
      <UserProfileModal
        isOpen={Boolean(viewingUserProfileUid)}
        onClose={() => setViewingUserProfileUid(null)}
        targetUserUidOrUsername={viewingUserProfileUid}
        currentUserUid={userUid}
      />

      {/* ABOUT THE CREATOR MODAL */}
      {isAboutModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-lg rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl text-[#FAFAFA] space-y-5 max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setIsAboutModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-between gap-4 pr-8">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#1C1C1C] border-2 border-amber-500/40 overflow-hidden shadow-lg shrink-0">
                  <img
                    src="/assets/aistudio/photo_6ee47b86.jpg"
                    alt="Adonis Melencion"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                    Adonis Melencion
                  </h3>
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                    Creator &amp; Developer
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-center gap-1.5 shrink-0 pl-2">
                <img
                  src="/quickibible-icon.png"
                  alt="QuickiBible Official Q! Brand Logo"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-contain shadow-xl border border-amber-500/30"
                />
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-[#A3A3A3] font-semibold">
                  <span>Quick<span className="text-[#F97316]">!</span>Bible</span>
                  <span className="text-[#F59E0B] text-[7px] font-light font-serif tracking-widest uppercase">BETA</span>
                </span>
              </div>
            </div>

            <div className="p-4 sm:p-5 rounded-xl bg-[#141414] border border-[#262626] text-xs sm:text-sm text-[#D4D4D4] leading-relaxed space-y-3.5 font-sans">
              <p>
                QuickiBible is a personal hobby project created and maintained by Adonis Melencion.
              </p>
              <p>
                Adonis is also a musician, pianist, keyboardist, and Christian music producer. He has released various instrumental piano, gospel, worship, and Christian music projects, including Christianong Pianista, Tagalog Christian Piano Hymns, Devotion, and Christian Sax Serenade.
              </p>
              <p>
                Built with passion to bring disciples, students, churches, and scholars together to engage Scripture with joy, reverence, and speed across all devices globally.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setIsAboutModalOpen(false)}
                className="px-5 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold transition-colors shadow-md cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUPPORT QUICK!BIBLE MODAL */}
      {isSupportModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-lg rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl text-[#FAFAFA] space-y-5">
            <button
              type="button"
              onClick={() => setIsSupportModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                <HeartHandshake className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Support Quick!Bible</h3>
                <p className="text-xs text-[#A3A3A3]">Love gifts &amp; voluntary support are deeply appreciated.</p>
              </div>
            </div>

            <p className="text-xs text-[#D4D4D4] leading-relaxed">
              Your voluntary gifts sustain high-performance cloud servers, real-time multiplayer arenas, and continuous public-domain Scripture verification.
            </p>

            <div className="space-y-3 pt-1">
              {/* PayPal */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-[#141414] border border-[#262626] text-xs">
                <div className="space-y-0.5">
                  <span className="font-semibold text-[#A3A3A3] block text-[11px]">PayPal</span>
                  <span className="font-mono text-white font-medium select-all">
                    adonizchoy@gmail.com
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => copySupportInfo('adonizchoy@gmail.com', 'paypal')}
                  className="px-3 py-1.5 rounded-lg bg-[#262626] hover:bg-[#737373] text-[#FAFAFA] font-semibold flex items-center gap-1.5 transition-colors"
                >
                  {copiedSupport === 'paypal' ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-emerald-400">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              {/* GCash */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-[#141414] border border-[#262626] text-xs">
                <div className="space-y-0.5">
                  <span className="font-semibold text-[#A3A3A3] block text-[11px]">GCash</span>
                  <span className="font-mono text-white font-medium select-all">
                    09508828535
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => copySupportInfo('09508828535', 'gcash')}
                  className="px-3 py-1.5 rounded-lg bg-[#262626] hover:bg-[#737373] text-[#FAFAFA] font-semibold flex items-center gap-1.5 transition-colors"
                >
                  {copiedSupport === 'gcash' ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-emerald-400">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setIsSupportModalOpen(false)}
                className="px-5 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold transition-colors shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <OfflineIndicator />
    </div>
    </ProfileProvider>
  );
}
